import React from 'react';
import ReactDOM from 'react-dom';
import bemBlock from '@gik/core/utils/bemBlock';
import { useInkind } from '@gik/api/inkinds/inkind';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';
import {
  Button,
  FontIcon,
  Form,
  FormField,
  Heading,
  IButtonProps,
  LinkParser,
  LoadingSpinner,
  ParagraphBone,
  Separator,
  UI,
} from '@gik/ui';
import { inkindPageTourSelectors } from '@gik/inkind-page';
import InkindUserUpdates from '@gik/inkind-page/components/InkindUserUpdates/InkindUserUpdates';
import { inkindPageInfoSchema } from '@gik/inkind-page/formSchemas';
import { updatePageInformation } from '@gik/inkind-page/api/InkindPageAPI';
import getRandomInt from '@gik/core/utils/getRandomInt';
import repeatElement from '@gik/core/utils/repeatElement';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { PageInformationInputNames } from '@gik/inkind-page/enums/PageInformationInputNames';

interface IEditInkindPageInformationForm {
  [PageInformationInputNames.TITLE]: string;
  [PageInformationInputNames.STORY]: string;
  [PageInformationInputNames.NOTES]: string;
}

export interface IInkindPageInformationProps {
  inkindRouteId: string;
}

const blockName = 'inkind-page-information';

export default function InkindPageInformation({ inkindRouteId }: IInkindPageInformationProps) {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);
  const { data: inkindPage, revalidate: revalidateInkindPage } = useInkind(inkindRouteId);
  const canEdit = canUserEditPage(inkindPage, userId);
  const schema = inkindPageInfoSchema();
  const buttonsRef = React.useRef<HTMLDivElement>();
  const formValues = React.useRef<IEditInkindPageInformationForm>();
  const [loading, setLoading] = React.useState<boolean>(false);

  async function handleUpdateInkindPageInfo(valid: boolean) {
    if (!valid) {
      return;
    }

    setLoading(true);

    await updatePageInformation(inkindPage.routeId, {
      title: formValues.current[PageInformationInputNames.TITLE],
      description: formValues.current[PageInformationInputNames.STORY],
      specialNotes: formValues.current[PageInformationInputNames.NOTES],
    });

    formValues.current = null;
    setLoading(false);
    revalidateInkindPage();

    UI.closeDialog();
  }

  async function openEditInkindPageInformationModal() {
    function setFormValues(values) {
      formValues.current = values;
    }

    UI.dialog(
      <div className={bem('edit-inkind-page-information')}>
        {loading && <LoadingSpinner />}
        {!loading && (
          <Form
            initialValues={{
              [PageInformationInputNames.TITLE]: inkindPage.title,
              [PageInformationInputNames.STORY]: inkindPage.description,
              [PageInformationInputNames.NOTES]: inkindPage.notes,
            }}
            onChange={setFormValues}
            schema={schema}
            vertical
            render={({ valid }) => {
              const buttons = (
                <>
                  <Button variant={'default'} onClick={() => UI.closeDialog()}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleUpdateInkindPageInfo(valid)} disabled={!valid}>
                    Save
                  </Button>
                </>
              );

              return (
                <>
                  {schema.map((field, key) => (
                    <FormField key={key} className={bem('form-field')} name={field.name} center />
                  ))}

                  {buttonsRef?.current ? ReactDOM.createPortal(buttons, buttonsRef.current) : buttons}
                </>
              );
            }}
          />
        )}
      </div>,
      {
        title: t(translationKeys.EditInkindPageInformationModalTitle),
        buttons: <div ref={buttonsRef} />,
      }
    );
  }

  return (
    <div className={bem()}>
      <InkindUserUpdates routeId={inkindRouteId} />

      <div>
        <div className={bem('story-wrapper')}>
          <div className={bem('title-wrapper')}>
            {canEdit && inkindPage?.description && (
              <EditProfileInformationIconButton
                id={inkindPageTourSelectors.addStory}
                openEditInkindPageInformationModal={openEditInkindPageInformationModal}
              />
            )}
            <Heading level={2}>{t(translationKeys.InkindPageInformationStoryLabel)}</Heading>
          </div>
          {inkindPage?.description ? (
            <div className={bem('intro')}>
              <p>
                <LinkParser>{inkindPage?.description}</LinkParser>
              </p>
            </div>
          ) : !canEdit ? (
            inkindPage ? (
              <div className={bem('intro', [{ empty: true }])}>
                <p>{t(translationKeys.InkindPageInformationIntroEmptyCopy)}</p>
              </div>
            ) : (
              <div className={bem('intro')}>
                <PageStorySkeleton />
              </div>
            )
          ) : (
            <div className={bem('intro', [{ empty: true }, { organizer: true }])}>
              <EditProfileInformationTextButton
                text={t(translationKeys.EditProfileInformationTextButtonPageDescriptionLabel)}
                openEditInkindPageInformationModal={openEditInkindPageInformationModal}
              />
            </div>
          )}
        </div>
      </div>

      <Separator thickness={'double'} type={'dashed'} />

      <div className={bem('special-notes-wrapper')}>
        <div className={bem('title-wrapper')}>
          {canEdit && inkindPage?.notes && (
            <EditProfileInformationIconButton
              isAddSpecialNotes={true}
              openEditInkindPageInformationModal={openEditInkindPageInformationModal}
            />
          )}
          <Heading level={2} variant={'danger'}>
            {t(translationKeys.InkindPageInformationSpecialNotesLabel)}
          </Heading>
        </div>
        {inkindPage?.notes ? (
          <div className={bem('special-notes-content')}>
            <p>
              <LinkParser>{inkindPage?.notes}</LinkParser>
            </p>
          </div>
        ) : !canEdit ? (
          inkindPage ? (
            <div className={bem('special-notes-content', [{ empty: true }])}>
              <p>{t(translationKeys.InkindPageInformationSpecialNotesEmptyCopy)}</p>
            </div>
          ) : (
            <div className={bem('special-notes-content')}>
              <PageStorySkeleton />
            </div>
          )
        ) : (
          <div className={bem('special-notes-content', [{ empty: true }, { organizer: true }])}>
            <EditProfileInformationTextButton
              text={t(translationKeys.EditProfileInformationTextButtonSpecialNotesLabel)}
              isAddSpecialNotes={true}
              openEditInkindPageInformationModal={openEditInkindPageInformationModal}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface IEditProfileInformationIconButtonProps extends IButtonProps {
  isAddSpecialNotes?: boolean;
  openEditInkindPageInformationModal: (isAddSpecialNotes: boolean) => void;
}

function EditProfileInformationIconButton({
  isAddSpecialNotes = false,
  openEditInkindPageInformationModal,
  ...buttonProps
}: IEditProfileInformationIconButtonProps) {
  const bem = bemBlock('edit-profile-information-icon-button');
  const { t } = useTranslation();

  return (
    <Button
      {...buttonProps}
      className={bem()}
      variant={'secondary'}
      onClick={() => openEditInkindPageInformationModal(isAddSpecialNotes)}
      size={'sm'}
      tooltip={t(translationKeys.EditProfileInformationIconButtonTooltip)}
      circle
    >
      <FontIcon name={'edit'} />
    </Button>
  );
}

interface IEditProfileInformationTextButtonProps {
  text: string;
  isAddSpecialNotes?: boolean;
  openEditInkindPageInformationModal: (isAddSpecialNotes: boolean) => void;
}

function EditProfileInformationTextButton({
  text,
  isAddSpecialNotes = false,
  openEditInkindPageInformationModal,
}: IEditProfileInformationTextButtonProps) {
  const bem = bemBlock('edit-profile-information-text-button');

  return (
    <Button
      className={bem()}
      variant={'secondary'}
      onClick={() => openEditInkindPageInformationModal(isAddSpecialNotes)}
      prepend={<FontIcon name={'edit'} />}
    >
      {text}
    </Button>
  );
}

function PageStorySkeleton() {
  function getParagraph() {
    return <ParagraphBone words={getRandomInt(7, 20)} />;
  }

  return <>{repeatElement(getRandomInt(4, 9), getParagraph())}</>;
}
